import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { currentPlanApi } from '../../../apis/payments.api'
import { useDarkMode } from '../../../hooks/useDarkMode'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'

export default function RenewableDate() {
  const { isDarkMode } = useDarkMode()
  const navigate = useNavigate()
  const handleApiError = useApiErrorHandler()
  const [planData, setPlanData] = useState(null)
  const [autoRenewal, setAutoRenewal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchPlanData()
  }, [handleApiError])

  const fetchPlanData = async () => {
    try {
      setLoading(true)
      const response = await currentPlanApi()
      setPlanData(response.data)
      setAutoRenewal(response.data.auto_renew)
      setError(null)
    } catch (err) {
      // Check if it's an auth error - handleApiError will handle redirect
      const isAuthError = handleApiError(err)
      if (!isAuthError) {
        const status = err.response?.status
        if (status === 404) {
          setPlanData(null)
        } else {
          setError(err.message || 'Failed to fetch plan details')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePurchasePlan = () => {
    navigate('/dashboard/pricing')
  }

  const handleChangeBillingCycle = () => {
    navigate('/dashboard/pricing', { state: { billingCycle: 'yearly' } })
  }

  if (loading) {
    return (
      <div className={`p-6 w-4/5 mx-auto rounded-2xl shadow my-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors duration-200 shadow-sm ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white'}`}
          >
            <FaArrowLeft className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Auto Renewal Settings</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`p-6 w-4/5 mx-auto rounded-2xl shadow my-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors duration-200 shadow-sm ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white'}`}
          >
            <FaArrowLeft className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Auto Renewal Settings</h1>
        </div>
        <div className={`text-lg ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
          Error: {error}
        </div>
      </div>
    )
  }

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div className={`p-4 sm:p-6 w-[95%] sm:w-4/5 mx-auto rounded-2xl shadow my-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors duration-200 shadow-sm ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-white'}`}
        >
          <FaArrowLeft className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
        </button>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Renewable Date</h1>
      </div>

      {planData ? (
        <>
          {/* Gray Card with Dates */}
          <div className={`rounded-xl p-6 mb-8 shadow-sm ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {/* Purchased On Row */}
            <div className={`flex items-center justify-between mb-6 pb-6 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
              <span className={`text-base sm:text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Purchased on</span>
              <span className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{planData.purchase_date}</span>
            </div>

            {/* Renewal Date Row */}
            <div className="flex items-center justify-between">
              <span className={`text-base sm:text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Renewal date</span>
              <span className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{planData.expiry_date}</span>
            </div>
          </div>

          {/* Billing Cycle Heading */}
          <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Billing Cycle</h2>

          {/* Billing Cycle Card */}
          <div className={`rounded-xl p-6 mb-8 shadow-sm ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center justify-between">
              <span className={`text-base sm:text-lg font-medium capitalize ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{planData.billing_cycle}</span>
              <button onClick={handleChangeBillingCycle} className="text-blue-600 font-semibold hover:underline">Change Now</button>
            </div>
          </div>

          {/* Auto Renewal Section */}
          <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Auto Renewal</h2>

          <div className="flex items-center justify-between">
            <span className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Auto Renewal</span>
            
            {/* Toggle Button */}
            <button
              onClick={() => setAutoRenewal(!autoRenewal)}
              className={`relative inline-flex items-center w-16 h-8 rounded-full transition-colors ${
                autoRenewal ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-400'
              }`}
            >
              <span
                className={`inline-block w-7 h-7 bg-white rounded-full shadow-md transform transition-transform ${
                  autoRenewal ? 'translate-x-9' : 'translate-x-0'
                }`}
              />
              <span className={`absolute inset-0 flex items-center mx-2 ${
                autoRenewal ? 'justify-start' : 'justify-end'
              }`}>
                <span className="text-xs font-semibold text-white">
                  {autoRenewal ? 'On' : 'Off'}
                </span>
              </span>
            </button>
          </div>
        </>
      ) : (
        <div className={`rounded-xl p-8 text-center ${isDarkMode ? 'bg-gradient-to-b from-gray-700 to-gray-800' : 'bg-gradient-to-b from-gray-100 to-gray-200'}`}>
          <div className="mb-6">
            <svg 
              className={`mx-auto h-16 w-16 mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              No Renewable Date Available
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              You haven't purchased any plan yet. No renewable date information is available.
            </p>
          </div>
          <button 
            onClick={handlePurchasePlan}
            className="bg-[#008300] hover:bg-[#006600] text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
          >
            Purchase a Plan
          </button>
        </div>
      )}
    </div></>
  )
}